import { resolveListingHourlyDailyPrices } from '../utils/assetBookingPricingOptions';
import type { AppLanguage } from '../i18n/types';
import { normalizeVoiceText, removeVoicePhrase } from './matchVoiceSearchCity';

export type AssistantPriceUnit = 'hour' | 'day';
export type AssistantPriceCompare = 'lt' | 'lte' | 'gt' | 'gte' | 'eq';

export type AssistantPriceConstraint = {
  amount: number;
  unit: AssistantPriceUnit;
  compare: AssistantPriceCompare;
};

/** Browse / last_search fields for `POST /assistant/turn`. */
export type AssistantPriceWireFields = {
  price_amount: number | null;
  price_unit: AssistantPriceUnit | null;
  price_compare: AssistantPriceCompare | null;
  max_price_per_hour: number | null;
  min_price_per_hour: number | null;
  max_price_per_day: number | null;
  min_price_per_day: number | null;
};

const COMPARE_PHRASES: Array<{ phrase: string; compare: AssistantPriceCompare }> = [
  { phrase: 'no more than', compare: 'lte' },
  { phrase: 'or cheaper', compare: 'lte' },
  { phrase: 'or less', compare: 'lte' },
  { phrase: 'less than', compare: 'lt' },
  { phrase: 'cheaper than', compare: 'lt' },
  { phrase: 'or more', compare: 'gte' },
  { phrase: 'at most', compare: 'lte' },
  { phrase: 'up to', compare: 'lte' },
  { phrase: 'greater than', compare: 'gt' },
  { phrase: 'more than', compare: 'gt' },
  { phrase: 'at least', compare: 'gte' },
  { phrase: 'equal to', compare: 'eq' },
  { phrase: 'exactly', compare: 'eq' },
  { phrase: 'equals', compare: 'eq' },
  { phrase: 'או פחות', compare: 'lte' },
  { phrase: 'או יותר', compare: 'gte' },
  { phrase: 'מתחת ל', compare: 'lt' },
  { phrase: 'פחות מ', compare: 'lt' },
  { phrase: 'יותר מ', compare: 'gt' },
  { phrase: 'שווה ל', compare: 'eq' },
  { phrase: 'לכל היותר', compare: 'lte' },
  { phrase: 'לפחות', compare: 'gte' },
  { phrase: 'מתחת', compare: 'lt' },
  { phrase: 'פחות', compare: 'lt' },
  { phrase: 'מעל', compare: 'gt' },
  { phrase: 'יותר', compare: 'gt' },
  { phrase: 'בדיוק', compare: 'eq' },
  { phrase: 'שווה', compare: 'eq' },
  { phrase: 'בול', compare: 'eq' },
  { phrase: 'under', compare: 'lt' },
  { phrase: 'below', compare: 'lt' },
  { phrase: 'over', compare: 'gt' },
  { phrase: 'above', compare: 'gt' },
  { phrase: 'until', compare: 'lte' },
  { phrase: 'עד', compare: 'lte' },
];
COMPARE_PHRASES.sort((a, b) => b.phrase.length - a.phrase.length);

const UNIT_PHRASES: Array<{ phrase: string; unit: AssistantPriceUnit }> = [
  { phrase: 'per hour', unit: 'hour' },
  { phrase: 'an hour', unit: 'hour' },
  { phrase: 'a hour', unit: 'hour' },
  { phrase: 'hourly', unit: 'hour' },
  { phrase: 'per day', unit: 'day' },
  { phrase: 'a day', unit: 'day' },
  { phrase: 'daily', unit: 'day' },
  { phrase: 'לשעה', unit: 'hour' },
  { phrase: 'ליום', unit: 'day' },
  { phrase: '/hr', unit: 'hour' },
  { phrase: '/hour', unit: 'hour' },
  { phrase: '/day', unit: 'day' },
  { phrase: 'hour', unit: 'hour' },
  { phrase: 'day', unit: 'day' },
];
UNIT_PHRASES.sort((a, b) => b.phrase.length - a.phrase.length);

const CURRENCY_PHRASES = ['shekels', 'shekel', 'sheqel', 'שח', 'nis', 'ils', '₪'].sort(
  (a, b) => b.length - a.length,
);

function normalizePriceText(value: string): string {
  return normalizeVoiceText(value)
    .replace(/[""„‟«»]/g, ' ')
    .replace(/ש\s*["'`״׳]?\s*ח/g, 'שח')
    .replace(/₪/g, ' ₪ ')
    .replace(/\s+/g, ' ')
    .trim();
}

function matchListedPhrase<T extends { phrase: string }>(
  hay: string,
  entries: T[],
): { entry: T; phrase: string } | null {
  const normalized = normalizePriceText(hay);
  for (const entry of entries) {
    const needle = normalizePriceText(entry.phrase);
    if (!needle) {
      continue;
    }
    const next = removeVoicePhrase(normalized, needle);
    if (next !== normalized) {
      return { entry, phrase: needle };
    }
  }
  return null;
}

function parseAmount(text: string): { amount: number; phrase: string } | null {
  const hay = ` ${normalizePriceText(text)} `;
  const matched = hay.match(/[^\d.](\d{1,6}(?:[.,]\d{1,2})?)[^\d.]/);
  if (!matched?.[1]) {
    return null;
  }
  const amount = Number(matched[1].replace(',', '.'));
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  return { amount, phrase: matched[1] };
}

/** “עד לסוף שנת 2026” / “until end of 2026” — calendar years, not ₪ amounts. */
function stripCalendarYearPhrases(text: string): string {
  return normalizePriceText(text)
    .replace(/\b(?:סוף\s+)?שנת\s+\d{4}\b/gi, ' ')
    .replace(/\buntil\s+(?:the\s+)?end\s+of\s+(?:the\s+)?(?:year\s+)?\d{4}\b/gi, ' ')
    .replace(/\bend\s+of\s+(?:the\s+)?(?:year\s+)?\d{4}\b/gi, ' ')
    .replace(/\byear\s+\d{4}\b/gi, ' ')
    .replace(/\bעד\s+(?:ל)?סוף(?:\s+שנ[הת])?\b/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function looksLikeCalendarYearAmount(amount: number, hay: string): boolean {
  if (amount < 1900 || amount > 2100 || !Number.isInteger(amount)) {
    return false;
  }
  return /(?:שנת|year|סוף|end of)/i.test(hay) || /(?:^|\s)עד(?:\s|$)/i.test(hay);
}

export function parseAssistantPrice(text: string): {
  constraint: AssistantPriceConstraint | null;
  remainder: string;
} {
  const original = normalizePriceText(text);
  if (!original) {
    return { constraint: null, remainder: '' };
  }
  // Parse on year-stripped text so “עד … שנת 2026” does not become price ≤ 2026.
  let remainder = stripCalendarYearPhrases(original);
  if (!remainder) {
    return { constraint: null, remainder: original };
  }
  const compareHit = matchListedPhrase(remainder, COMPARE_PHRASES);
  const unitHit = matchListedPhrase(remainder, UNIT_PHRASES);
  const currencyHit = matchListedPhrase(
    remainder,
    CURRENCY_PHRASES.map((phrase) => ({ phrase })),
  );
  const amountHit = parseAmount(remainder);
  if (!amountHit || (!compareHit && !unitHit && !currencyHit)) {
    return { constraint: null, remainder: original };
  }
  if (!currencyHit && looksLikeCalendarYearAmount(amountHit.amount, original)) {
    return { constraint: null, remainder: original };
  }
  const constraint: AssistantPriceConstraint = {
    amount: amountHit.amount,
    unit: unitHit?.entry.unit ?? 'hour',
    compare: compareHit?.entry.compare ?? 'eq',
  };
  // Strip matched price tokens from the original utterance (keep year phrases for when/fillers).
  let out = original;
  const strip = [compareHit?.phrase, amountHit.phrase, currencyHit?.phrase, unitHit?.phrase].filter(
    (value): value is string => Boolean(value),
  );
  for (const phrase of strip) {
    // Do not strip a bare year that is part of “שנת 2026”.
    if (/^\d{4}$/.test(phrase) && looksLikeCalendarYearAmount(Number(phrase), original)) {
      continue;
    }
    out = removeVoicePhrase(out, phrase);
  }
  out = out.replace(/^[לבמ]\s+/, '').replace(/\s+/g, ' ').trim();
  return { constraint, remainder: out };
}

export function emptyAssistantPriceWire(): AssistantPriceWireFields {
  return {
    price_amount: null,
    price_unit: null,
    price_compare: null,
    max_price_per_hour: null,
    min_price_per_hour: null,
    max_price_per_day: null,
    min_price_per_day: null,
  };
}

export function assistantPriceToWire(
  price: AssistantPriceConstraint | null | undefined,
): AssistantPriceWireFields {
  const empty = emptyAssistantPriceWire();
  if (!price || !Number.isFinite(price.amount) || price.amount <= 0) {
    return empty;
  }
  const amount = price.amount;
  empty.price_amount = amount;
  empty.price_unit = price.unit;
  empty.price_compare = price.compare;
  if (price.unit === 'hour') {
    if (price.compare === 'lt' || price.compare === 'lte') {
      empty.max_price_per_hour = amount;
    } else if (price.compare === 'gt' || price.compare === 'gte') {
      empty.min_price_per_hour = amount;
    } else {
      empty.min_price_per_hour = amount;
      empty.max_price_per_hour = amount;
    }
  } else if (price.compare === 'lt' || price.compare === 'lte') {
    empty.max_price_per_day = amount;
  } else if (price.compare === 'gt' || price.compare === 'gte') {
    empty.min_price_per_day = amount;
  } else {
    empty.min_price_per_day = amount;
    empty.max_price_per_day = amount;
  }
  return empty;
}

function finitePositive(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string' && value.trim()) {
    const n = Number(value.trim().replace(',', '.'));
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return null;
}

function parseUnit(value: unknown): AssistantPriceUnit | null {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (raw === 'hour' || raw === 'hourly' || raw === 'hr') {
    return 'hour';
  }
  if (raw === 'day' || raw === 'daily') {
    return 'day';
  }
  return null;
}

function parseCompare(value: unknown): AssistantPriceCompare | null {
  const raw = typeof value === 'string' ? value.trim().toLowerCase() : '';
  if (raw === 'lte' || raw === 'atmost' || raw === 'at_most' || raw === 'max') {
    return 'lte';
  }
  if (raw === 'gte' || raw === 'atleast' || raw === 'at_least' || raw === 'min') {
    return 'gte';
  }
  if (raw === 'lt' || raw === 'less' || raw === 'under' || raw === 'below') {
    return 'lt';
  }
  if (raw === 'gt' || raw === 'more' || raw === 'over' || raw === 'above') {
    return 'gt';
  }
  if (raw === 'eq' || raw === 'equal' || raw === 'equals' || raw === 'exact') {
    return 'eq';
  }
  return null;
}

function fromMinMax(
  min: number | null,
  max: number | null,
  unit: AssistantPriceUnit,
): AssistantPriceConstraint | null {
  if (min != null && max != null) {
    if (min === max) {
      return { amount: min, unit, compare: 'eq' };
    }
    return null;
  }
  if (max != null) {
    return { amount: max, unit, compare: 'lte' };
  }
  if (min != null) {
    return { amount: min, unit, compare: 'gte' };
  }
  return null;
}

/** Accept the structured triple or the min/max hour/day aliases. */
export function parseAssistantPriceFromBrowse(value: Record<string, unknown> | null | undefined): AssistantPriceConstraint | null {
  if (!value) {
    return null;
  }
  const amount = finitePositive(value.price_amount ?? value.priceAmount);
  const unit = parseUnit(value.price_unit ?? value.priceUnit);
  const compare = parseCompare(value.price_compare ?? value.priceCompare);
  if (amount != null && unit && compare) {
    return { amount, unit, compare };
  }
  return (
    fromMinMax(
      finitePositive(value.min_price_per_hour ?? value.minPricePerHour),
      finitePositive(value.max_price_per_hour ?? value.maxPricePerHour),
      'hour',
    ) ??
    fromMinMax(
      finitePositive(value.min_price_per_day ?? value.minPricePerDay),
      finitePositive(value.max_price_per_day ?? value.maxPricePerDay),
      'day',
    )
  );
}

export function listingMatchesAssistantPrice(
  asset: Record<string, unknown>,
  price: AssistantPriceConstraint | null | undefined,
): boolean {
  if (!price) {
    return true;
  }
  const { hourly, daily } = resolveListingHourlyDailyPrices(asset);
  const rate = price.unit === 'day' ? daily : hourly;
  if (rate === null || !Number.isFinite(rate) || rate < 0) {
    return false;
  }
  if (price.compare === 'lt') {
    return rate < price.amount;
  }
  if (price.compare === 'lte') {
    return rate <= price.amount;
  }
  if (price.compare === 'gt') {
    return rate > price.amount;
  }
  if (price.compare === 'gte') {
    return rate >= price.amount;
  }
  return Math.abs(rate - price.amount) < 0.005;
}

/** Prefer a local “or less / or more” bound when the turn used a strict compare on the same amount. */
export function preferInclusiveAssistantPrice(
  turnPrice: AssistantPriceConstraint | null | undefined,
  localPrice: AssistantPriceConstraint | null | undefined,
): AssistantPriceConstraint | null {
  if (!localPrice) {
    return turnPrice ?? null;
  }
  if (!turnPrice) {
    return localPrice;
  }
  if (localPrice.amount !== turnPrice.amount || localPrice.unit !== turnPrice.unit) {
    return turnPrice;
  }
  if (localPrice.compare === 'lte' && turnPrice.compare === 'lt') {
    return localPrice;
  }
  if (localPrice.compare === 'gte' && turnPrice.compare === 'gt') {
    return localPrice;
  }
  return turnPrice;
}

export function formatAssistantPriceChip(
  price: AssistantPriceConstraint | null | undefined,
  language: AppLanguage = 'en',
): string | null {
  if (!price) {
    return null;
  }
  const amount = Number.isInteger(price.amount) ? String(price.amount) : String(price.amount);
  if (language === 'he') {
    const unit = price.unit === 'day' ? 'ליום' : 'לשעה';
    if (price.compare === 'lt') {
      return `מתחת ל-${amount} ₪ ${unit}`;
    }
    if (price.compare === 'lte') {
      return `עד ${amount} ₪ ${unit}`;
    }
    if (price.compare === 'gt') {
      return `מעל ${amount} ₪ ${unit}`;
    }
    if (price.compare === 'gte') {
      return `לפחות ${amount} ₪ ${unit}`;
    }
    return `${amount} ₪ ${unit}`;
  }
  const unit = price.unit === 'day' ? '/day' : '/hr';
  if (price.compare === 'lt') {
    return `under ${amount} ₪${unit}`;
  }
  if (price.compare === 'lte') {
    return `up to ${amount} ₪${unit}`;
  }
  if (price.compare === 'gt') {
    return `over ${amount} ₪${unit}`;
  }
  if (price.compare === 'gte') {
    return `at least ${amount} ₪${unit}`;
  }
  return `${amount} ₪${unit}`;
}

export function assistantPriceConstraintsEqual(
  a: AssistantPriceConstraint | null | undefined,
  b: AssistantPriceConstraint | null | undefined,
): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  return a.amount === b.amount && a.unit === b.unit && a.compare === b.compare;
}

export function parseAssistantPriceFromUrl(raw: string | null | undefined): AssistantPriceConstraint | null {
  const value = raw?.trim() ?? '';
  const matched = value.match(/^(lte|gte|lt|gt|eq)-(hour|day)-(\d+(?:\.\d+)?)$/i);
  if (!matched) {
    return null;
  }
  const amount = Number(matched[3]);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  return {
    compare: matched[1].toLowerCase() as AssistantPriceCompare,
    unit: matched[2].toLowerCase() as AssistantPriceUnit,
    amount,
  };
}

export function assistantPriceToUrl(price: AssistantPriceConstraint | null | undefined): string | null {
  if (!price) {
    return null;
  }
  return `${price.compare}-${price.unit}-${price.amount}`;
}
